import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { GraduationCap, ArrowLeft, Loader2, Award, CheckCircle2, XCircle, Timer, ChevronRight, BookOpen, Target, TrendingUp, AlertTriangle, Lightbulb, Home, Lock, ShoppingCart, ExternalLink, Trophy, Medal, Clock, Send, Eye, RotateCcw, ArrowRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
// Topics fetched from LMS Manager API
import MathText from "@/components/MathText";
import FloatingBackButton from "@/components/FloatingBackButton";
import PremiumPopup from "@/components/PremiumPopup";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// TGT Subjects
const tgtSubjects = [
  { value: "hindi", label: "Hindi" },
  { value: "english", label: "English" },
  { value: "mathematics", label: "Mathematics" },
  { value: "science", label: "Science" },
  { value: "social_science", label: "Social Science" },
  { value: "computer_science", label: "Computer Science" },
  { value: "physical_education", label: "Physical Education" },
  { value: "music", label: "Music" },
  { value: "art", label: "Art/Drawing" }
];

// PGT Subjects
const pgtSubjects = [
  { value: "hindi", label: "Hindi" },
  { value: "english", label: "English" },
  { value: "mathematics", label: "Mathematics" },
  { value: "physics", label: "Physics" },
  { value: "chemistry", label: "Chemistry" },
  { value: "biology", label: "Biology" },
  { value: "commerce", label: "Commerce" },
  { value: "economics", label: "Economics" },
  { value: "geography", label: "Geography" },
  { value: "history", label: "History" },
  { value: "computer_science", label: "Computer Science" },
  { value: "music", label: "Music" },
  { value: "art", label: "Art/Fine Arts" }
];

// Non-Teaching Subjects
const nonTeachingSubjects = [
  { value: "hostel_warden", label: "Hostel Warden" },
  { value: "library_science", label: "Library Science" }
];

// Topics fetched dynamically from LMS Manager API

export default function MCQPractice({ user, subjectSelection, onLogin }) {
  const [searchParams] = useSearchParams();
  // For all-subjects exams (KVS PRT), subject='all' means browse subjects first
  const isAllSubjects = subjectSelection?.subject === 'all';
  // Step tracking: 1=exam type, 2=subject, 3=topic, 4=loading, 5=test, 6=result
  const [step, setStep] = useState(() => {
    if (!subjectSelection) return 1;
    if (isAllSubjects) return 2; // KVS PRT: pick subject first
    return 3;
  });
  const [examType, setExamType] = useState(() => subjectSelection?.category || "");
  const [subject, setSubject] = useState(() => (subjectSelection?.subject && subjectSelection.subject !== 'all') ? subjectSelection.subject : "");
  const [topic, setTopic] = useState("");
  const [mcqs, setMcqs] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [currentFact, setCurrentFact] = useState("");
  const [facts, setFacts] = useState([]);
  
  // Premium popup state
  const [showPremiumPopup, setShowPremiumPopup] = useState(false);
  
  // Leaderboard state
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);
  
  // Timer state - 20 minutes = 1200 seconds (20 questions)
  const [timeLeft, setTimeLeft] = useState(1200);
  const [timerActive, setTimerActive] = useState(false);

  // Auto-fill from URL params (coming from Notes page)
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current) return;
    const et = searchParams.get('exam_type');
    const sub = searchParams.get('subject');
    const top = searchParams.get('topic');
    if (et && sub && top) {
      prefilled.current = true;
      setExamType(et);
      setSubject(sub);
      setTopic(top);
      setStep(4);
      setLoading(true);
      
      const pollPrefilled = async (retries = 0) => {
        try {
          const res = await axios.post(`${API}/mcq/get-questions`, {
            exam_type: et, subject: sub, topic: top, user_id: user.id
          });
          if (res.data.generating && retries < 24) {
            await new Promise(r => setTimeout(r, 5000));
            return pollPrefilled(retries + 1);
          }
          setMcqs(res.data.mcqs);
          setSelectedAnswers({});
          setCurrentQuestion(0);
          setTimeLeft(res.data.mcqs.length * 60);
          startTimeRef.current = Date.now();
          setStep(5);
          setTimerActive(true);
        } catch (err) {
          if (err.response?.status === 502 && retries < 24) {
            await new Promise(r => setTimeout(r, 5000));
            return pollPrefilled(retries + 1);
          }
          toast.error("Failed to load questions. Please try again.");
          setStep(subjectSelection ? 3 : 1);
        } finally {
          setLoading(false);
        }
      };
      pollPrefilled();
    }
  }, [searchParams, subjectSelection]);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const factIntervalRef = useRef(null);

  // Fetch facts on component mount
  useEffect(() => {
    const fetchFacts = async () => {
      try {
        const response = await axios.get(`${API}/facts/all`);
        if (response.data.success && response.data.facts.length > 0) {
          setFacts(response.data.facts);
          setCurrentFact(response.data.facts[Math.floor(Math.random() * response.data.facts.length)]);
        }
      } catch (error) {
        console.log("Facts not loaded");
      }
    };
    fetchFacts();
  }, []);

  // Rotate facts during loading
  useEffect(() => {
    if (step === 4 && facts.length > 0) {
      factIntervalRef.current = setInterval(() => {
        setCurrentFact(facts[Math.floor(Math.random() * facts.length)]);
      }, 4000);
    }
    return () => {
      if (factIntervalRef.current) {
        clearInterval(factIntervalRef.current);
      }
    };
  }, [step, facts]);

  // Timer effect
  useEffect(() => {
    if (timerActive && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            // Auto-submit when time runs out
            handleAutoSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [timerActive]);

  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle exam type selection
  const handleExamTypeSelect = (type) => {
    setExamType(type);
    setSubject("");
    setTopic("");
    setStep(2);
  };

  // Handle subject selection
  const handleSubjectSelect = (value) => {
    setSubject(value);
    setTopic("");
    setStep(3);
  };

  // Handle topic selection and fetch MCQs (with polling for background generation)
  const handleTopicSelect = async (value) => {
    setTopic(value);
    setStep(4);
    setLoading(true);
    
    const pollForQuestions = async (retries = 0) => {
      try {
        const response = await axios.post(`${API}/mcq/get-questions`, {
          exam_type: examType,
          subject: subject,
          topic: value,
          user_id: user.id
        });
        
        // If backend is still generating, poll again
        if (response.data.generating) {
          if (retries < 24) { // Max ~2 minutes of polling (24 * 5s)
            await new Promise(r => setTimeout(r, 5000));
            return pollForQuestions(retries + 1);
          } else {
            toast.error("Question generation is taking too long. Please try again.");
            setStep(3);
            setLoading(false);
            return;
          }
        }
        
        setMcqs(response.data.mcqs);
        setSelectedAnswers({});
        setCurrentQuestion(0);
        setTimeLeft(response.data.mcqs.length * 60); // 1 min per question
        startTimeRef.current = Date.now();
        setStep(5);
        setTimerActive(true);
      } catch (error) {
        // On 502 timeout, the backend may still be generating — retry
        if (error.response?.status === 502 && retries < 24) {
          await new Promise(r => setTimeout(r, 5000));
          return pollForQuestions(retries + 1);
        }
        toast.error("Failed to load questions. Please try again.");
        setStep(3);
      } finally {
        setLoading(false);
      }
    };
    
    await pollForQuestions();
  };

  // Handle answer selection
  const handleAnswerSelect = (questionIndex, answer) => {
    setSelectedAnswers(prev => ({
      ...prev,
      [questionIndex]: answer
    }));
  };

  // Navigate questions
  const goToQuestion = (index) => {
    if (index >= 0 && index < mcqs.length) {
      setCurrentQuestion(index);
    }
  };

  // Auto submit when timer ends
  const handleAutoSubmit = () => {
    toast.warning("Time's up! Submitting your answers...");
    submitAnswers();
  };

  // Submit answers
  const submitAnswers = async () => {
    setTimerActive(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    setStep(6);
    setLoading(true);
    
    const timeTaken = Math.floor((Date.now() - startTimeRef.current) / 1000);
    
    // Prepare answers with question details
    const answersData = mcqs.map((mcq, index) => ({
      question_id: mcq.id,
      question: mcq.question,
      selected_answer: selectedAnswers[index] || "",
      correct_answer: mcq.correct_answer,
      key_info: mcq.key_info,
      question_type: mcq.question_type || "standard",
      match_data: mcq.match_data || null
    }));
    
    try {
      const response = await axios.post(`${API}/mcq/submit`, {
        user_id: user.id,
        exam_type: examType,
        subject: subject,
        topic: topic,
        answers: answersData,
        time_taken: timeTaken
      });
      
      setResult(response.data);
      toast.success("Test submitted successfully!");
    } catch (error) {
      toast.error("Failed to submit. Please try again.");
      setStep(5);
      setTimerActive(true);
    } finally {
      setLoading(false);
    }
  };

  // Manual submit
  const handleSubmit = () => {
    const unanswered = mcqs.length - Object.keys(selectedAnswers).length;
    if (unanswered > 0) {
      if (!window.confirm(`You have ${unanswered} unanswered question(s). Submit anyway?`)) {
        return;
      }
    }
    submitAnswers();
  };

  // Reset and start new test
  const handleNewTest = () => {
    setStep(1);
    setExamType("");
    setSubject("");
    setTopic("");
    setMcqs([]);
    setSelectedAnswers({});
    setCurrentQuestion(0);
    setResult(null);
    setTimeLeft(0);
    setTimerActive(false);
  };

  // Reattempt: restart with same exam/subject/topic
  const handleReattempt = () => {
    setMcqs([]);
    setSelectedAnswers({});
    setCurrentQuestion(0);
    setResult(null);
    setTimeLeft(0);
    setTimerActive(false);
    setStep(3); // go back to topic selection (same exam + subject preselected)
  };

  // View Solutions: show step 7
  const handleViewSolutions = () => {
    setStep(7);
  };

  const fetchLeaderboard = async () => {
    if (!result || !examType || !subject || !topic) return;
    
    setLoadingLeaderboard(true);
    try {
      const response = await axios.get(`${API}/mcq/leaderboard`, {
        params: {
          exam_type: examType,
          subject: subject,
          topic: topic,
          limit: 50
        }
      });
      
      if (response.data.success) {
        setLeaderboardData(response.data);
        setShowLeaderboard(true);
      }
    } catch (error) {
      console.error("Failed to fetch leaderboard:", error);
      toast.error("Failed to load leaderboard");
    } finally {
      setLoadingLeaderboard(false);
    }
  };


  const currentSubjects = examType === "tgt" ? tgtSubjects : examType === "pgt" ? pgtSubjects : nonTeachingSubjects;

  // Dynamic subjects from LMS API for non-TGT/PGT exams (KVS PRT etc.)
  const [lmsSubjects, setLmsSubjects] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  useEffect(() => {
    if (!examType) { setLmsSubjects([]); return; }
    setLoadingSubjects(true);
    axios.get(`${API}/notes/lms/syllabus/exams/${examType}/subjects`)
      .then(res => setLmsSubjects(res.data.subjects || []))
      .catch(() => setLmsSubjects([]))
      .finally(() => setLoadingSubjects(false));
  }, [examType]);
  const SUBJECT_SPECIFIC_EXAMS = ['tgt', 'pgt', 'non_teaching'];
  const useHardcodedSubjects = SUBJECT_SPECIFIC_EXAMS.includes(examType);
  const displaySubjects = useHardcodedSubjects
    ? currentSubjects.map(s => ({ id: s.value, name: s.label }))
    : lmsSubjects;
  const getSubjectLabel = () => displaySubjects.find(s => s.id === subject)?.name || subject;

  // Dynamic topics from LMS Manager API
  const [currentTopics, setCurrentTopics] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(false);

  // Topic progress tracking (MCQ section)
  const [coveredTopics, setCoveredTopics] = useState(new Set());

  const fetchTopicsFromAPI = useCallback(async (examTypeVal, subjectVal) => {
    if (!examTypeVal || !subjectVal) {
      setCurrentTopics([]);
      return;
    }
    setLoadingTopics(true);
    try {
      const res = await axios.get(`${API}/notes/lms/syllabus/exams/${examTypeVal}/subjects/${subjectVal}/topics`);
      const topics = res.data.topics || [];
      const formattedTopics = topics
        .filter(t => t && t !== '__placeholder__')
        .map(t => ({ value: t, label: t }));
      setCurrentTopics(formattedTopics);
    } catch (err) {
      console.error("Failed to fetch topics:", err);
      setCurrentTopics([]);
    } finally {
      setLoadingTopics(false);
    }
  }, []);

  // Fetch topics + coverage when exam type or subject changes
  useEffect(() => {
    if (examType && subject) {
      fetchTopicsFromAPI(examType, subject);
      // Fetch covered topics for MCQ section
      if (user?.id) {
        axios.get(`${API}/progress?user_id=${encodeURIComponent(user.id)}&exam_type=${encodeURIComponent(examType)}&subject=${encodeURIComponent(subject)}&section=mcq`)
          .then(r => setCoveredTopics(new Set(r.data.covered || [])))
          .catch(() => {});
      }
    } else {
      setCurrentTopics([]);
    }
  }, [examType, subject, fetchTopicsFromAPI]);

  const answeredCount = Object.keys(selectedAnswers).length;

  // Check if user is free (for topic-level locking - first topic free, rest locked)
  const isFreeUser = !user.plan || user.plan === 'free';
  
  // Helper to check if a topic is locked (only first topic is free for free users)
  const isTopicLocked = (topicIdx) => {
    return isFreeUser && topicIdx > 0;
  };
  
  // Wrap the original topic select with paywall check
  const handleTopicSelectWithPaywall = (topicValue, topicIdx) => {
    if (isTopicLocked(topicIdx)) {
      setShowPremiumPopup(true);
      return;
    }
    // Mark topic as covered in MCQ section
    if (user?.id) {
      axios.post(`${API}/progress/mark`, {
        user_id: user.id, exam_type: examType, subject, topic: topicValue, section: "mcq"
      }).then(() => setCoveredTopics(prev => new Set([...prev, topicValue]))).catch(() => {});
    }
    handleTopicSelect(topicValue);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <Link to="/dashboard" className="flex items-center gap-2 text-gray-600 hover:text-gray-800">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <Target className="w-8 h-8 text-green-600" />
              <span className="text-xl font-bold bg-gradient-to-r from-green-600 to-teal-600 bg-clip-text text-transparent">
                MCQ Practice
              </span>
            </div>
            {step === 5 && (
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full font-bold ${timeLeft <= 60 ? 'bg-red-100 text-red-700 animate-pulse' : timeLeft <= 180 ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
                <Timer className="w-5 h-5" />
                {formatTime(timeLeft)}
              </div>
            )}
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Progress Indicator */}
        {step < 5 && (
          <div className="mb-8">
            <div className="flex items-center justify-between max-w-xl mx-auto">
              {['Exam Type', 'Subject', 'Topic', 'Test'].map((label, idx) => (
                <div key={idx} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step > idx + 1 ? 'bg-green-500 text-white' : step === idx + 1 ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                    {step > idx + 1 ? '✓' : idx + 1}
                  </div>
                  {idx < 3 && <div className={`w-12 md:w-20 h-1 ${step > idx + 1 ? 'bg-green-500' : 'bg-gray-200'}`} />}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 1: Select Exam Type */}
        {step === 1 && (
          <Card className="shadow-xl">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">Select Exam Type</CardTitle>
              <p className="text-gray-600">Choose the category for your MCQ practice</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button
                  onClick={() => handleExamTypeSelect("tgt")}
                  className="h-32 text-lg bg-gradient-to-br from-green-500 to-green-700 hover:from-green-600 hover:to-green-800"
                  data-testid="mcq-select-tgt"
                >
                  <div className="text-center">
                    <GraduationCap className="w-10 h-10 mx-auto mb-2" />
                    <div className="font-bold">TGT</div>
                    <div className="text-sm opacity-90">Trained Graduate Teacher</div>
                    <div className="text-xs opacity-75 mt-1">Up to Class 10th Level</div>
                  </div>
                </Button>
                <Button
                  onClick={() => handleExamTypeSelect("pgt")}
                  className="h-32 text-lg bg-gradient-to-br from-teal-500 to-teal-700 hover:from-teal-600 hover:to-teal-800"
                  data-testid="mcq-select-pgt"
                >
                  <div className="text-center">
                    <Award className="w-10 h-10 mx-auto mb-2" />
                    <div className="font-bold">PGT</div>
                    <div className="text-sm opacity-90">Post Graduate Teacher</div>
                    <div className="text-xs opacity-75 mt-1">Class 11th-12th Level</div>
                  </div>
                </Button>
                <Button
                  onClick={() => handleExamTypeSelect("non_teaching")}
                  className="h-32 text-lg bg-gradient-to-br from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700"
                  data-testid="mcq-select-non-teaching"
                >
                  <div className="text-center">
                    <Home className="w-10 h-10 mx-auto mb-2" />
                    <div className="font-bold">Non-Teaching</div>
                    <div className="text-sm opacity-90">Hostel Warden & Others</div>
                    <div className="text-xs opacity-75 mt-1">Administrative Staff</div>
                  </div>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Select Subject */}
        {step === 2 && (
          <Card className="shadow-xl">
            <CardHeader className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Badge className={examType === "tgt" ? "bg-green-600" : examType === "pgt" ? "bg-teal-600" : "bg-amber-600"}>
                  {examType.replace(/_/g, ' ').toUpperCase()}
                </Badge>
                {isAllSubjects && <Badge className="bg-green-600">All Subjects</Badge>}
              </div>
              <CardTitle className="text-2xl">Select Subject</CardTitle>
              <p className="text-gray-600">Choose your subject for MCQ practice</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {loadingSubjects ? (
                  <div className="col-span-3 py-6 text-center text-gray-400">Loading subjects...</div>
                ) : displaySubjects.length === 0 ? (
                  <div className="col-span-3 py-6 text-center text-gray-400">No subjects found.</div>
                ) : (
                  displaySubjects.map((subj) => (
                    <Button
                      key={subj.id}
                      variant="outline"
                      onClick={() => handleSubjectSelect(subj.id)}
                      className="h-16 hover:bg-green-50 hover:border-green-400"
                      data-testid={`mcq-select-subject-${subj.id}`}
                    >
                      <span className="font-semibold">{subj.name}</span>
                    </Button>
                  ))
                )}
              </div>
              {!isAllSubjects && (
                <Button variant="ghost" onClick={() => setStep(1)} className="mt-4">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 3: Select Topic */}
        {step === 3 && (
          <Card className="shadow-xl">
            <CardHeader className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Badge className={examType === "tgt" ? "bg-green-600" : "bg-teal-600"}>
                  {examType.replace(/_/g, ' ').toUpperCase()}
                </Badge>
                <Badge variant="outline">{getSubjectLabel()}</Badge>
              </div>
              <CardTitle className="text-2xl">Select Topic</CardTitle>
              <p className="text-gray-600">Choose a topic for MCQs (1 min per question)</p>
            </CardHeader>
            <CardContent>
              {loadingTopics ? (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-green-500 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">Loading topics...</p>
                </div>
              ) : currentTopics.length > 0 ? (
                <div className="space-y-2">
                  {currentTopics.map((t, idx) => {
                    const locked = isTopicLocked(idx);
                    const isFree = idx === 0;
                    const isCovered = coveredTopics.has(t.value);
                    const glowStyle = isCovered
                      ? { outline: '2px solid #22c55e', boxShadow: '0 0 14px rgba(34,197,94,0.35)' }
                      : { outline: '1.5px solid rgba(239,68,68,0.45)', boxShadow: '0 0 8px rgba(239,68,68,0.18)' };
                    return (
                      <Button
                        key={idx}
                        variant="outline"
                        style={glowStyle}
                        onClick={() => handleTopicSelectWithPaywall(t.value, idx)}
                        className={`w-full justify-between h-auto py-3 px-4 text-left ${locked ? 'opacity-70 hover:bg-orange-50' : 'hover:bg-green-50'}`}
                        data-testid={`mcq-select-topic-${idx}`}
                      >
                        <div className="flex items-center">
                          <ChevronRight className="w-4 h-4 mr-2 flex-shrink-0" />
                          <span>{t.label}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {isCovered && <span className="text-[10px] font-bold text-green-600 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">Covered</span>}
                          {isFree && !isCovered && <Badge className="bg-green-100 text-green-700 text-xs">Free</Badge>}
                          {locked && <Lock className="w-4 h-4 text-orange-500" />}
                        </div>
                      </Button>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-gray-500">No topics available</p>
              )}
              
              {/* Paywall info for free users */}
              {isFreeUser && currentTopics.length > 1 && (
                <Card className="mt-4 border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50">
                  <CardContent className="py-4 text-center">
                    <Lock className="w-6 h-6 mx-auto mb-2 text-orange-500" />
                    <p className="text-sm text-gray-600">First topic is free! Unlock all topics with</p>
                    <p className="font-bold text-orange-700">Premium Plan @ ₹499</p>
                  </CardContent>
                </Card>
              )}
              
              <Button variant="ghost" onClick={() => { setSubject(""); setStep(2); }} className="mt-4">
                <ArrowLeft className="w-4 h-4 mr-2" /> {isAllSubjects ? "Browse Other Subjects" : "Back"}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Loading with Facts */}
        {step === 4 && (
          <Card className="shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-teal-600 text-white p-6 text-center">
              <Loader2 className="w-12 h-12 animate-spin mx-auto mb-3" />
              <h2 className="text-2xl font-bold mb-1">Dear Student 👋</h2>
              <p className="text-green-100">We are loading questions for you...</p>
            </div>
            <CardContent className="p-6">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Badge className={examType === "tgt" ? "bg-green-600" : examType === "pgt" ? "bg-teal-600" : "bg-orange-600"}>
                  {examType === "non_teaching" ? "NON-TEACHING" : examType.toUpperCase()}
                </Badge>
                <Badge variant="outline">{getSubjectLabel()}</Badge>
                <Badge variant="outline">{topic}</Badge>
              </div>
              
              {/* Did You Know Section */}
              {currentFact && (
                <div className="bg-gradient-to-br from-yellow-50 to-amber-50 border-2 border-yellow-200 rounded-xl p-5 mt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Lightbulb className="w-6 h-6 text-yellow-600" />
                    <h3 className="font-bold text-yellow-800 text-lg">Do You Know? 💡</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">{currentFact}</p>
                </div>
              )}
              
              <div className="flex justify-center mt-4">
                <div className="flex gap-1">
                  {[0,1,2].map(i => (
                    <div key={i} className="w-2 h-2 rounded-full bg-green-400 animate-bounce" style={{animationDelay: `${i * 0.2}s`}} />
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 5: MCQ Test */}
        {step === 5 && mcqs.length > 0 && (
          <div className="space-y-6">
            {/* Progress Bar */}
            <div className="bg-white rounded-xl p-4 shadow-md">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">
                  Question {currentQuestion + 1} of {mcqs.length}
                </span>
                <span className="text-sm font-medium text-gray-600">
                  {answeredCount}/{mcqs.length} Answered
                </span>
              </div>
              <Progress value={(answeredCount / mcqs.length) * 100} className="h-2" />
            </div>

            {/* Question Navigation Pills */}
            <div className="bg-white rounded-xl p-4 shadow-md">
              <div className="flex flex-wrap gap-2 justify-center">
                {mcqs.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => goToQuestion(idx)}
                    className={`w-10 h-10 rounded-full font-bold text-sm transition-all ${
                      currentQuestion === idx
                        ? 'bg-green-600 text-white scale-110'
                        : selectedAnswers[idx]
                        ? 'bg-green-100 text-green-700 border-2 border-green-400'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {idx + 1}
                  </button>
                ))}
              </div>
            </div>

            {/* Current Question */}
            <Card className="shadow-xl">
              <CardHeader className="bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-t-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-white/20">Question {currentQuestion + 1}</Badge>
                    {mcqs[currentQuestion].question_type && mcqs[currentQuestion].question_type !== 'standard' && (
                      <Badge className={
                        mcqs[currentQuestion].question_type === 'match' ? 'bg-purple-500/80 text-white' :
                        mcqs[currentQuestion].question_type === 'assertion_reason' ? 'bg-amber-500/80 text-white' :
                        mcqs[currentQuestion].question_type === 'fill_blank' ? 'bg-blue-500/80 text-white' :
                        mcqs[currentQuestion].question_type === 'statement_conclusion' ? 'bg-rose-500/80 text-white' :
                        'bg-white/20'
                      }>
                        {mcqs[currentQuestion].question_type === 'match' ? 'Match the Following' :
                         mcqs[currentQuestion].question_type === 'assertion_reason' ? 'Assertion & Reasoning' :
                         mcqs[currentQuestion].question_type === 'fill_blank' ? 'Fill in the Blank' :
                         mcqs[currentQuestion].question_type === 'statement_conclusion' ? 'Statement & Conclusion' :
                         'MCQ'}
                      </Badge>
                    )}
                  </div>
                  <Badge className="bg-white/20 max-w-[200px] truncate">{topic}</Badge>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {/* Match the Following — Table Display */}
                {mcqs[currentQuestion].question_type === 'match' && mcqs[currentQuestion].match_data ? (
                  <div className="mb-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-4">
                      <MathText text={mcqs[currentQuestion].question.split('\n')[0]} />
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse rounded-lg overflow-hidden shadow-sm">
                        <thead>
                          <tr className="bg-gradient-to-r from-purple-100 to-purple-50">
                            <th className="border border-purple-200 px-4 py-3 text-left font-bold text-purple-800 w-8">#</th>
                            <th className="border border-purple-200 px-4 py-3 text-left font-bold text-purple-800">Column A</th>
                            <th className="border border-purple-200 px-4 py-3 text-left font-bold text-purple-800 w-8">#</th>
                            <th className="border border-purple-200 px-4 py-3 text-left font-bold text-purple-800">Column B</th>
                          </tr>
                        </thead>
                        <tbody>
                          {mcqs[currentQuestion].match_data.col_a.map((item, i) => (
                            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="border border-gray-200 px-4 py-3 font-bold text-purple-600">{i + 1}.</td>
                              <td className="border border-gray-200 px-4 py-3 text-gray-800"><MathText text={item} /></td>
                              <td className="border border-gray-200 px-4 py-3 font-bold text-purple-600">{String.fromCharCode(97 + i)}.</td>
                              <td className="border border-gray-200 px-4 py-3 text-gray-800"><MathText text={mcqs[currentQuestion].match_data.col_b[i] || ''} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <h3 className="text-xl font-bold text-gray-800 mb-6 leading-relaxed whitespace-pre-line">
                    <MathText text={mcqs[currentQuestion].question} />
                  </h3>
                )}
                
                <div className="space-y-3">
                  {mcqs[currentQuestion].options.map((option, optIdx) => (
                    <button
                      key={optIdx}
                      onClick={() => handleAnswerSelect(currentQuestion, option)}
                      data-testid={`mcq-option-${optIdx}`}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                        selectedAnswers[currentQuestion] === option
                          ? 'border-green-500 bg-green-50 ring-2 ring-green-200'
                          : 'border-gray-200 hover:border-green-300 hover:bg-green-50/50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0 ${
                          selectedAnswers[currentQuestion] === option
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {String.fromCharCode(65 + optIdx)}
                        </span>
                        <span className="flex-1"><MathText text={option} /></span>
                        {selectedAnswers[currentQuestion] === option && (
                          <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Navigation Buttons */}
            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={() => goToQuestion(currentQuestion - 1)}
                disabled={currentQuestion === 0}
                className="flex-1"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Previous
              </Button>
              
              <Button
                onClick={handleSubmit}
                className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
              >
                <Send className="w-4 h-4 mr-2" /> Submit Test
              </Button>
              
              <Button
                onClick={() => goToQuestion(currentQuestion + 1)}
                disabled={currentQuestion === mcqs.length - 1}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                Next <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 6: Loading Results */}
        {step === 6 && loading && (
          <Card className="shadow-xl">
            <CardContent className="py-16 text-center">
              <Loader2 className="w-16 h-16 animate-spin text-green-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Calculating Results...</h2>
              <p className="text-gray-600">Please wait</p>
            </CardContent>
          </Card>
        )}

        {/* Step 6: Results */}
        {step === 6 && !loading && result && (
          <div className="space-y-6">
            {/* Score Card */}
            <Card className="shadow-xl overflow-hidden">
              <div className={`p-6 text-center text-white ${
                result.percentage >= 80 ? 'bg-gradient-to-r from-green-500 to-emerald-600' :
                result.percentage >= 60 ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
                'bg-gradient-to-r from-red-500 to-pink-600'
              }`}>
                <h2 className="text-lg opacity-90 mb-2">Your Score</h2>
                <div className="text-6xl font-bold mb-2">{result.score}/{result.total}</div>
                <div className="text-2xl opacity-90">{result.percentage}%</div>
                <Badge className="mt-3 bg-white/20 text-lg px-4 py-1">{result.performance}</Badge>
              </div>
              <CardContent className="p-6">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="bg-green-50 rounded-lg p-4">
                    <TrendingUp className="w-6 h-6 text-green-600 mx-auto mb-1" />
                    <div className="text-2xl font-bold text-green-600">#{result.rank || result.total_attempts || 1}</div>
                    <div className="text-sm text-gray-600">Rank</div>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <Target className="w-6 h-6 text-blue-600 mx-auto mb-1" />
                    <div className="text-2xl font-bold text-blue-600">{result.percentile}%</div>
                    <div className="text-sm text-gray-600">Percentile</div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <Timer className="w-6 h-6 text-purple-600 mx-auto mb-1" />
                    <div className="text-2xl font-bold text-purple-600">{formatTime(result.time_taken)}</div>
                    <div className="text-sm text-gray-600">Time</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button 
                onClick={handleViewSolutions}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white py-6 text-lg font-semibold shadow-lg"
                data-testid="view-solutions-btn"
              >
                <Eye className="w-5 h-5 mr-2" /> View Solutions
              </Button>
              <Button
                onClick={handleReattempt}
                className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white py-6 text-lg font-semibold"
                data-testid="reattempt-practice-btn"
              >
                <RotateCcw className="w-5 h-5 mr-2" /> Reattempt
              </Button>
              <Button 
                onClick={fetchLeaderboard}
                disabled={loadingLeaderboard}
                className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white py-6 text-lg font-semibold shadow-lg"
              >
                {loadingLeaderboard ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading...</>
                ) : (
                  <><Trophy className="w-5 h-5 mr-2" /> View Leaderboard</>
                )}
              </Button>
              <Button
                onClick={handleNewTest}
                variant="outline"
                className="w-full py-6 text-lg"
              >
                <Home className="w-4 h-4 mr-2" /> Practice New Topic
              </Button>
            </div>

            {/* Topics to Review */}
            {result.topics_to_review && result.topics_to_review.length > 0 && (
              <Card className="shadow-lg border-2 border-orange-200">
                <CardHeader className="bg-orange-50">
                  <CardTitle className="text-lg flex items-center gap-2 text-orange-700">
                    <AlertTriangle className="w-5 h-5" />
                    Topics to Review Again
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-3">
                  {result.topics_to_review.map((item, idx) => (
                    <div key={idx} className="bg-orange-50/50 rounded-lg p-4 border border-orange-100">
                      <p className="font-medium text-gray-800 mb-2"><MathText text={item.question} /></p>
                      <div className="flex items-start gap-2">
                        <Lightbulb className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-gray-600"><MathText text={item.key_info} /></p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Back to Dashboard */}
            <Link to="/dashboard" className="w-full block">
              <Button variant="outline" className="w-full">
                <Home className="w-4 h-4 mr-2" /> Back to Dashboard
              </Button>
            </Link>

            {/* Leaderboard Modal */}
            {showLeaderboard && leaderboardData && (
              <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={() => setShowLeaderboard(false)}>
                <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white" onClick={(e) => e.stopPropagation()}>
                  <CardHeader className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white sticky top-0 z-10">
                    <CardTitle className="flex items-center gap-2 text-2xl">
                      <Trophy className="w-6 h-6" />
                      Leaderboard - Top Performers
                    </CardTitle>
                    <p className="text-sm text-white/90 mt-1">
                      {leaderboardData.topic} • {leaderboardData.total_participants} participants
                    </p>
                  </CardHeader>
                  <CardContent className="p-6">
                    {/* Top 10 */}
                    <div className="space-y-3 mb-6">
                      {leaderboardData.leaderboard.slice(0, 10).map((entry, idx) => (
                        <div 
                          key={entry.user_id}
                          className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                            entry.user_id === user.id 
                              ? 'bg-blue-50 border-blue-400 shadow-md' 
                              : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {/* Rank */}
                          <div className="flex-shrink-0 w-12 text-center">
                            {idx === 0 && <span className="text-3xl">🥇</span>}
                            {idx === 1 && <span className="text-3xl">🥈</span>}
                            {idx === 2 && <span className="text-3xl">🥉</span>}
                            {idx > 2 && (
                              <div className="text-xl font-bold text-gray-600">#{entry.rank}</div>
                            )}
                          </div>

                          {/* User Info */}
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-900 truncate">
                              {entry.user_name}
                              {entry.user_id === user.id && (
                                <Badge className="ml-2 bg-blue-500 text-white text-xs">You</Badge>
                              )}
                            </p>
                            <div className="flex items-center gap-3 text-sm text-gray-600 mt-1">
                              <span className="flex items-center gap-1">
                                <Target className="w-3 h-3" /> {entry.percentage}%
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {formatTime(entry.time_taken)}
                              </span>
                            </div>
                          </div>

                          {/* Score */}
                          <div className="text-right">
                            <div className="text-2xl font-bold text-green-600">{entry.score}</div>
                            <div className="text-xs text-gray-500">/ {entry.total}</div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* User's Position (if rank > 10) */}
                    {result && result.rank > 10 && (
                      <div className="border-t-2 border-gray-300 pt-4">
                        <p className="text-sm text-gray-500 font-medium mb-3 text-center">Your Position</p>
                        <div className="flex items-center gap-3 p-4 rounded-lg bg-blue-100 border-2 border-blue-400 shadow-md">
                          <div className="flex-shrink-0 w-12 text-center">
                            <div className="text-xl font-bold text-blue-600">#{result.rank}</div>
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900">{user.name}</p>
                            <div className="flex items-center gap-3 text-sm text-gray-600 mt-1">
                              <span>{result.percentage}%</span>
                              <span>•</span>
                              <span>{formatTime(result.time_taken)}</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-bold text-green-600">{result.score}</div>
                            <div className="text-xs text-gray-500">/ {result.total}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Percentile Distribution */}
                    <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
                      <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" />
                        Your Performance
                      </h3>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Percentile</span>
                          <span className="font-bold text-purple-600">{result?.percentile || 0}%</span>
                        </div>
                        <Progress value={result?.percentile || 0} className="h-3" />
                        <p className="text-xs text-gray-500 text-center mt-2">
                          You performed better than {result?.percentile || 0}% of participants
                        </p>
                      </div>
                    </div>

                    <Button onClick={() => setShowLeaderboard(false)} className="w-full mt-6">
                      Close
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}

        {/* Step 7: Detailed Solutions View */}
        {step === 7 && result && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <button 
                onClick={() => setStep(6)} 
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
                data-testid="solutions-back-btn"
              >
                <ArrowLeft className="w-5 h-5" /> Back to Results
              </button>
              <h2 className="text-xl font-bold text-gray-800">Detailed Solutions</h2>
            </div>

            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-200 mb-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  <span className="font-semibold">{result.score}/{result.total}</span> correct ({result.percentage}%)
                </div>
                <div className="flex gap-3 text-sm">
                  <span className="flex items-center gap-1 text-green-600"><CheckCircle2 className="w-4 h-4" /> {result.score}</span>
                  <span className="flex items-center gap-1 text-red-600"><XCircle className="w-4 h-4" /> {result.total - result.score}</span>
                </div>
              </div>
            </div>

            {mcqs.map((mcq, idx) => {
              const r = result.results[idx];
              const isCorrect = r?.is_correct;
              const studentAnswer = selectedAnswers[idx];
              return (
                <Card key={idx} className={`shadow-md border-2 ${isCorrect ? 'border-green-200 bg-green-50/30' : 'border-red-200 bg-red-50/30'}`} data-testid={`solution-card-${idx}`}>
                  <CardContent className="p-5">
                    {/* Question Header */}
                    <div className="flex items-start gap-2 mb-3 flex-wrap">
                      <Badge className={`${isCorrect ? 'bg-green-600' : 'bg-red-600'} text-white`}>Q{idx + 1}</Badge>
                      {mcq.question_type && mcq.question_type !== 'standard' && (
                        <Badge variant="outline" className={
                          mcq.question_type === 'match' ? 'border-purple-400 text-purple-700' :
                          mcq.question_type === 'assertion_reason' ? 'border-amber-400 text-amber-700' :
                          mcq.question_type === 'fill_blank' ? 'border-blue-400 text-blue-700' :
                          mcq.question_type === 'statement_conclusion' ? 'border-rose-400 text-rose-700' : ''
                        }>
                          {mcq.question_type === 'match' ? 'Match the Following' :
                           mcq.question_type === 'assertion_reason' ? 'Assertion & Reasoning' :
                           mcq.question_type === 'fill_blank' ? 'Fill in the Blank' :
                           mcq.question_type === 'statement_conclusion' ? 'Statement & Conclusion' : ''}
                        </Badge>
                      )}
                      {isCorrect ? 
                        <CheckCircle2 className="w-5 h-5 text-green-600" /> : 
                        <XCircle className="w-5 h-5 text-red-600" />
                      }
                    </div>

                    {/* Question Text */}
                    {mcq.question_type === 'match' && mcq.match_data ? (
                      <div className="mb-4">
                        <p className="font-medium text-gray-800 mb-2"><MathText text={mcq.question.split('\n')[0]} /></p>
                        <table className="w-full border-collapse text-sm mb-2 rounded overflow-hidden">
                          <thead>
                            <tr className="bg-purple-50">
                              <th className="border border-purple-200 px-3 py-2 text-left font-semibold text-purple-700">#</th>
                              <th className="border border-purple-200 px-3 py-2 text-left font-semibold text-purple-700">Column A</th>
                              <th className="border border-purple-200 px-3 py-2 text-left font-semibold text-purple-700">#</th>
                              <th className="border border-purple-200 px-3 py-2 text-left font-semibold text-purple-700">Column B</th>
                            </tr>
                          </thead>
                          <tbody>
                            {mcq.match_data.col_a.map((item, i) => (
                              <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="border border-gray-200 px-3 py-2 font-bold text-purple-600">{i + 1}.</td>
                                <td className="border border-gray-200 px-3 py-2"><MathText text={item} /></td>
                                <td className="border border-gray-200 px-3 py-2 font-bold text-purple-600">{String.fromCharCode(97 + i)}.</td>
                                <td className="border border-gray-200 px-3 py-2"><MathText text={mcq.match_data.col_b[i] || ''} /></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="font-medium text-gray-800 mb-4 whitespace-pre-line"><MathText text={mcq.question} /></p>
                    )}

                    {/* All Options with correct/wrong highlighting */}
                    <div className="space-y-2 mb-4">
                      {mcq.options.map((option, optIdx) => {
                        const isThisCorrect = option === mcq.correct_answer;
                        const isStudentPick = option === studentAnswer;
                        const isWrongPick = isStudentPick && !isThisCorrect;
                        return (
                          <div 
                            key={optIdx} 
                            className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 text-sm ${
                              isThisCorrect 
                                ? 'bg-green-100 border-green-400 text-green-800 font-semibold' 
                                : isWrongPick 
                                  ? 'bg-red-100 border-red-400 text-red-800' 
                                  : 'bg-white border-gray-200 text-gray-700'
                            }`}
                          >
                            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                              isThisCorrect ? 'bg-green-600 text-white' : isWrongPick ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-600'
                            }`}>
                              {String.fromCharCode(65 + optIdx)}
                            </span>
                            <span className="flex-1"><MathText text={option} /></span>
                            {isThisCorrect && <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />}
                            {isWrongPick && <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />}
                          </div>
                        );
                      })}
                    </div>

                    {/* Not attempted indicator */}
                    {!studentAnswer && (
                      <p className="text-sm text-gray-500 italic mb-2">Not attempted</p>
                    )}

                    {/* Explanation */}
                    {(r?.key_info || mcq.key_info) && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                        <p className="text-sm text-blue-800">
                          <span className="font-bold flex items-center gap-1 mb-1"><Lightbulb className="w-4 h-4 text-blue-600" /> Explanation</span>
                          <MathText text={r?.key_info || mcq.key_info} />
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}

            {/* Bottom Action Buttons */}
            <div className="space-y-3 mt-6">
              <Button
                onClick={handleReattempt}
                className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white py-6 text-lg font-semibold"
                data-testid="reattempt-from-solutions-btn"
              >
                <RotateCcw className="w-5 h-5 mr-2" /> Reattempt
              </Button>
              <Link to="/dashboard" className="w-full block">
                <Button variant="outline" className="w-full py-5">
                  <Home className="w-4 h-4 mr-2" /> Back to Dashboard
                </Button>
              </Link>
            </div>
          </div>
        )}
      </main>
      
      {/* Floating Back Button */}
      <FloatingBackButton 
        onBack={
          step === 7 ? () => setStep(6) :
          step === 4 ? () => setStep(3) :
          step === 3 ? () => setStep(2) :
          step === 2 ? () => setStep(1) :
          topic ? () => { setTopic(""); setStep(1); } :
          subject ? () => { setSubject(""); setStep(1); } :
          examType ? () => { setExamType(""); setStep(1); } :
          null
        }
        showHome={true}
      />
      
      {/* Premium Popup for locked content */}
      {showPremiumPopup && (
        <PremiumPopup
          user={user}
          onClose={() => setShowPremiumPopup(false)}
          onUpgrade={onLogin}
        />
      )}
    </div>
  );
}
